<script setup>
import {
    Button,
    Table,
    TableColumn,
    Space,
    Popconfirm,
    Tooltip
} from 'ant-design-vue';
import {
    formatAmountCompact,
    formatAmountFull
} from '@/utils/balanceSheetDisplay';
import {
    calcMonthlyAmortization,
    formatMonthlyAmortization
} from '@/utils/flatBalanceSheetEditor';

const props = defineProps({
    classType: { type: Number, required: true },
    rows: { type: Array, default: () => [] },
    amountLabel: { type: String, default: '現值' },
    cashFlowLabel: { type: String, default: '月收入' },
    isReadonly: { type: Boolean, default: false },
    ve: { type: Object, required: true }
});

const optionLabel = (record) =>
    record.option_name ||
    props.ve.getOptionName(record.type_id, record.option_id, '');

const categoryLabel = (record) => {
    const type = props.ve.getTypeNameById(record.type_id);
    const opt = optionLabel(record);
    return opt && opt !== type ? `${type} · ${opt}` : type;
};

const fieldChips = (record) => props.ve.renderFieldChips(record.fields || []);

const monthlyAmortizationLabel = (record) => {
    if (props.classType !== 1) return '—';
    return formatMonthlyAmortization(
        calcMonthlyAmortization(record.amount, record.monthly_interest_expense)
    );
};
</script>

<template>
    <Table
        :data-source="rows"
        :pagination="false"
        row-key="data_id"
        size="middle"
        class="cw-item-table"
        :scroll="{ x: 640, y: 480 }"
    >
        <TableColumn title="類別" key="cat" :width="120" ellipsis>
            <template #default="{ record }">
                <span :title="categoryLabel(record)">{{
                    categoryLabel(record)
                }}</span>
            </template>
        </TableColumn>
        <TableColumn title="名稱" key="name" :width="72" ellipsis>
            <template #default="{ record }">
                <span :title="record.name">{{ record.name }}</span>
            </template>
        </TableColumn>
        <TableColumn
            :title="amountLabel"
            key="amount"
            :width="76"
            align="right"
        >
            <template #default="{ record }">
                <span
                    class="cw-amount"
                    :title="formatAmountFull(record.amount)"
                >
                    {{ formatAmountCompact(record.amount) }}
                </span>
            </template>
        </TableColumn>
        <TableColumn
            :title="cashFlowLabel"
            key="cf"
            :width="68"
            align="right"
        >
            <template #default="{ record }">
                <span
                    class="cw-amount"
                    :title="
                        formatAmountFull(record.monthly_interest_expense)
                    "
                >
                    {{
                        formatAmountCompact(record.monthly_interest_expense)
                    }}
                </span>
            </template>
        </TableColumn>
        <TableColumn
            v-if="classType === 1"
            title="月攤還"
            key="amort"
            :width="68"
            align="right"
        >
            <template #default="{ record }">
                <span
                    class="cw-amount cw-amount--muted"
                    title="月支出÷餘額，僅顯示不入庫"
                >
                    {{ monthlyAmortizationLabel(record) }}
                </span>
            </template>
        </TableColumn>
        <TableColumn title="詳細" key="details" :width="52" align="center">
            <template #default="{ record }">
                <Tooltip
                    v-if="fieldChips(record).length"
                    placement="topLeft"
                    :overlay-style="{ maxWidth: '320px' }"
                >
                    <template #title>
                        <div class="cw-detail-tip">
                            <div
                                v-for="chip in fieldChips(record)"
                                :key="chip"
                            >
                                {{ chip }}
                            </div>
                        </div>
                    </template>
                    <Button type="link" size="small" class="cw-detail-link">
                        {{ fieldChips(record).length }}項
                    </Button>
                </Tooltip>
                <span v-else class="cw-detail-empty">—</span>
            </template>
        </TableColumn>
        <TableColumn title="操作" key="act" :width="56" align="center" fixed="right">
            <template #default="{ record }">
                <Space :size="0">
                    <Button
                        v-if="isReadonly"
                        type="link"
                        size="small"
                        @click="ve.openViewItemModal(record)"
                    >
                        看
                    </Button>
                    <template v-else>
                        <Button
                            type="link"
                            size="small"
                            @click="ve.openEditModal(classType, record)"
                        >
                            改
                        </Button>
                        <Popconfirm
                            title="刪除？"
                            @confirm="ve.handleDeleteRow(classType, record)"
                        >
                            <Button type="link" size="small" danger>刪</Button>
                        </Popconfirm>
                    </template>
                </Space>
            </template>
        </TableColumn>
    </Table>
</template>

<style scoped>
.cw-amount {
    display: inline-block;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    font-size: 14px;
}
.cw-amount--muted {
    font-weight: 500;
    color: #667085;
}
.cw-detail-link {
    padding: 0 4px;
    height: auto;
    font-size: 13px;
}
.cw-detail-empty {
    color: #c0c4cc;
    font-size: 13px;
}
.cw-detail-tip {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 13px;
    line-height: 1.45;
}
.cw-item-table :deep(.ant-table-thead > tr > th) {
    font-size: 13px;
    font-weight: 600;
}
.cw-item-table :deep(.ant-table-cell) {
    padding: 10px 8px !important;
    font-size: 14px;
}
</style>
